import type { MotionPropsFor } from '@hover/blueprint';
import { forwardRef, motion } from '@hover/blueprint';
import React from 'react';
import { useHistory } from 'react-router';

import type { AiDisplayImageFragment } from 'src/shared/graphql/generated';
import { Route, generatePath } from 'src/shared/route';
import { imageClicked } from 'src/shared/telemetry';

import { useActiveOriginalImage, useHomeownerAppContext } from '../../hooks';
import { useImageOrientation } from '../../hooks/useImageOrientation';
import type { ImageCardProps } from '../ImageCard';
import { ImageCard } from '../ImageCard';
import { MasonryItem } from '../Masonry';

interface CardProps extends MotionPropsFor<ImageCardProps> {
  image: AiDisplayImageFragment;
}

const MotionImageCard = motion(ImageCard);

export const Card = forwardRef<CardProps, 'div'>(({ image, ...props }, ref) => {
  const { image: originalImage } = useActiveOriginalImage({
    originalImageId: image.aiOriginalImageId,
  });
  const history = useHistory();
  const { trackingData } = useHomeownerAppContext();

  const orientation = useImageOrientation({
    aiOriginalImageId: image.aiOriginalImageId,
  });

  return (
    <MasonryItem ref={ref}>
      <MotionImageCard
        cursor="pointer"
        display="inline-block"
        displayImage={image}
        onClick={() => {
          history.push(
            generatePath(Route.HOMEOWNER_APP_SAVED_DISPLAY_FULLSCREEN.path, {
              aiDisplayImageId: image.id,
            }),
          );

          imageClicked({
            ...trackingData,
            ai_generated_image_id: image?.aiGeneratedImage.id,
            ai_original_image_id: image?.aiOriginalImageId,
            track_id: 'favorites-full-screen',
            space_type: image?.spaceType || undefined,
          });
        }}
        orientation={orientation}
        spaceType={image.spaceType}
        styleName={image.aiGeneratedImage.style}
        subText={originalImage?.associatedProject?.name}
        width="100%"
        {...props}
      />
    </MasonryItem>
  );
});
